import { Component, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { isDefined } from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import {
    getFormFunctionalElementByIdFactory,
    getFormParticipantCopyReadonly,
    getFormSelectedParticipantCopyId,
    getFormTypeFilters,
} from '../../store/selectors/form.selector';
import { FormsState } from '../../store/state/pace-forms.state';
import { ContentId } from '../../types/content-block';
import { FormTypeFilters } from '../../types/form-type-filters';
import { FormsConstants } from '../../types/forms-constants';
import { Option } from '../../types/option';
import { ParticipantCopyId } from '../../types/participant-copy';

@Component({
    template: '',
})
export class BaseOptionComponent implements OnInit, OnDestroy {
    @Input()
    public id: ContentId;

    @Input()
    public parentForm: FormArray | undefined;

    public readOnly$: Observable<boolean>;
    public optionFunctionalElement$: Observable<Option>;
    public selectedParticipant$: Observable<ParticipantCopyId>;

    public typeFilters$: Observable<FormTypeFilters>;
    public visible$: Observable<boolean>;

    protected destroyTrigger$ = new Subject<void>();

    public optionGroup: FormGroup;
    public radioInputControl: FormControl;
    public constructor(
        @Inject(LogInstanceToken) protected readonly log: LogInterface,
        protected readonly store: Store<FormsState>,
        protected readonly formBuilder: FormBuilder,
    ) {
        this.readOnly$ = store.select(getFormParticipantCopyReadonly);
        this.typeFilters$ = store.select(getFormTypeFilters);
        this.selectedParticipant$ = store.select(getFormSelectedParticipantCopyId);
    }

    public ngOnInit(): void {
        this.radioInputControl = this.formBuilder.control('');
        this.optionGroup = this.formBuilder.group({ radio: this.radioInputControl });
        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.optionGroup);
                })
                .catch((error: Error) =>
                    this.log.error(
                        'OptionComponent: ngOnInit: Error in parentForm.push: ',
                        JSON.stringify(error, Object.getOwnPropertyNames(error)),
                    ),
                );
        }

        this.optionFunctionalElement$ = this.store
            .select(getFormFunctionalElementByIdFactory<Option>({ id: this.id }))
            .pipe(filter(isDefined));

        this.visible$ = combineLatest([this.optionFunctionalElement$, this.selectedParticipant$]).pipe(
            map(([option, selectedParticipant]) => {
                // The master displays everything. In a participant copy, we display only content that was filled out in
                // the current session and not loaded from XML.
                const visible =
                    selectedParticipant === FormsConstants.MASTER_PARTICIPANT_ID || !option.contentLoadedFromXml;

                return visible;
            }),
        );
    }

    public ngOnDestroy(): void {
        // remove form controls from parent
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.optionGroup);
            this.parentForm.removeAt(index);
        }
        this.destroyTrigger$.next();
    }

    public reset(): void {
        this.radioInputControl.reset();
    }
}
